/**
 * Danh mục hằng của nhóm màn Định mức & sản lượng.
 *
 * Mã module M16/M17/M18 chỉ còn dùng để tra ngược về tài liệu UISPEC, không đặt tên code nữa:
 * định mức thời gian (M16) · hoạch định năng lực (M17) · kiểm soát sản lượng (M18).
 *
 * Nhãn tiếng Việt lấy đúng từ FAC-M16-UISPEC-001 §0.5, FAC-M17-UISPEC-001 §3 và
 * FAC-M18-UISPEC-001 §H — không tự đặt lại chữ. Các chữ bị cấm hiện trên giao diện
 * (perItem, driver, độ phủ, rework, no_standard…) chỉ được dùng làm khoá dữ liệu.
 */

/**
 * Đường dẫn của 8 màn có route riêng.
 *
 * ⚠️ Tra cứu theo SKU và Nhập/xuất Excel KHÔNG còn ở đây: chúng là modal của
 *    `/config/standard-time`, mở bằng `?lookup=1` và `?mode=import|export`.
 *
 *    Hai đường dẫn cũ đó được `routes/view.js` bắt bằng redirect `exact` đặt TRƯỚC `:stageCode`,
 *    nên bookmark cũ vẫn mở đúng modal và giữ nguyên query (`?sku=`, `?mode=`). Nếu sau này bỏ hai
 *    redirect đó thì chúng sẽ rơi vào `:stageCode` và hiện LoadError vì service 404.
 */
export const ROUTES = {
    STANDARD_TIME_STAGES: '/config/standard-time',
    STANDARD_TIME_MISSING_SKU: '/config/standard-time/missing-sku',
    STANDARD_TIME_STAGE_CONFIG: '/config/standard-time/:stageCode',
    CAPACITY_PLANNING: '/planning/capacity',
    OUTPUT_REPORT: '/control/output',
    OUTPUT_DETAIL: '/control/output/detail',
    OUTPUT_SUSPECT: '/control/output/suspect',
    OUTPUT_GLOSSARY: '/control/output/glossary',
    /*
     * Màn NHẬP, không phải màn báo cáo — nhưng vẫn nằm dưới `/control/output/` vì đó đúng là
     * namespace của API (`GET|PUT control/output/actual-hours`), và quy ước của nhóm màn này là URL
     * trình duyệt đọc ra cùng một thứ với URL của service.
     *
     * ⚠️ `/control/output` khai `exact: true` ở `routes/sidebar.js`; mất `exact` thì đường này không
     *    bao giờ tới lượt. Cùng cái bẫy đã ghi cho `/control/output/by-stage`.
     */
    ACTUAL_HOURS: '/control/output/actual-hours',
    /*
     * Bản đồ mã nhân viên ↔ tài khoản. Nằm dưới `/control/` chứ không dưới `/control/output/`: đây
     * là danh mục CẤU HÌNH, không phải số liệu của báo cáo sản lượng — và đúng namespace API
     * (`GET control/worker-accounts`).
     */
    WORKER_ACCOUNTS: '/control/worker-accounts',
}

/**
 * Màn NGOÀI nhóm này nhưng được dẫn sang từ đây.
 *
 * Quy trình sản xuất của SKU do màn Quản lý SKU quản, không phải màn định mức — SKU chưa gán quy
 * trình thì phải sang đó xử lý. Khai ở đây để chỗ dùng không viết chuỗi thẳng, và đổi đường dẫn ở
 * routes/view.js thì có một nơi để sửa.
 */
export const EXTERNAL_ROUTES = {
    SKU_MANAGER: '/sku-manager',
}

/**
 * Màn chi tiết SKU — nơi thật sự gán được quy trình sản xuất.
 *
 * ⚠️ Nhận `_id` của Mongo, KHÔNG nhận mã SKU: `getSkuDetails` phía fulfillment-service chặn ngay
 *    bằng `isMongoId(id)`. Mà production-planning-service chỉ trả mã SKU, nên phải tra `_id` qua
 *    `sku/search` trước khi dùng — xem helpers/skuDetail.js.
 */
export const skuDetailPath = (skuObjectId) => `/sku-detail/${skuObjectId}`

export const stageConfigPath = (stageCode) => `/config/standard-time/${stageCode}`

/** Ba loại công đoạn — quyết định cách tính độ phủ (BR-M16-30). */
export const STAGE_TYPE_LABELS = {
    mandatory: {text: 'Bắt buộc chung', color: 'orange'},
    on_demand: {text: 'Theo yêu cầu', color: 'purple'},
    routing: {text: 'Theo quy trình', color: ''},
}

/** Nhịp công đoạn — hiển thị Máy / Người, không hiển thị machine / labor (§0.4). */
export const PACING_LABELS = {
    machine: {text: 'Máy', color: 'purple', long: 'Máy — nhịp do máy quyết định'},
    labor: {text: 'Người', color: 'green', long: 'Người — nhịp do tay người quyết định'},
}

/**
 * Đơn vị đếm sản lượng. M16 gọi là "Item / Đơn", M18 gọi là "Sản phẩm / Đơn hàng"
 * (M18 §H.4) — giữ cả hai vì hai tài liệu chốt hai bộ chữ khác nhau.
 */
export const COUNT_UNIT_LABELS = {
    item: {m16: 'Item', m18: 'Sản phẩm', color: 'blue'},
    order: {m16: 'Đơn', m18: 'Đơn hàng', color: 'purple'},
}

/** Cách tính giờ — độc lập tuyệt đối với đơn vị đếm (D-CR-17, RB-12). */
export const TIME_BASIS_LABELS = {
    by_item: {m16: 'Theo item/đơn', m18: 'Theo sản phẩm', color: ''},
    by_detect: {m16: 'Theo dữ liệu detect', m18: 'Theo dữ liệu file in', color: 'blue'},
}

/** Đại lượng lấy từ dòng NVL. `area` dùng chung cho In và Khắc (D-CR-18). */
export const DETECT_FIELD_LABELS = {
    area: 'area',
    cutting_length: 'cutting_length',
}

/** Đơn vị của chỉ số tiêu chuẩn, dẫn xuất từ (timeBasis, detectField, countUnit). */
export const RATE_UNIT_LABELS = {
    second_per_cm2: 'cm²',
    second_per_cm: 'cm',
    second_per_item: 'item',
    second_per_order: 'đơn',
}

/** Khối ví dụ G.24 của SCR-M16-002 — mỗi đơn vị đo một dòng mô tả riêng. */
export const RATE_UNIT_EXAMPLES = {
    second_per_cm2: {label: 'Ví dụ: 420 cm² × 50 item', quantity: 21000, unitText: 'cm²'},
    second_per_cm: {label: 'Ví dụ: 320 cm × 50 item', quantity: 16000, unitText: 'cm'},
    second_per_item: {label: 'Ví dụ: 50 item', quantity: 50, unitText: 'item'},
    second_per_order: {label: 'Ví dụ: 50 đơn', quantity: 50, unitText: 'đơn'},
}

/** Điểm độ ưu tiên (FR-M16-06 Bước 2). Nhóm ALL luôn 0 điểm và luôn thua mọi nhóm khác. */
export const PRIORITY_POINTS = {
    SKU_LIST: 50,
    MATERIAL: 10,
    ALL: 0,
}

/**
 * Trần độ ưu tiên nhập tay — khớp `PRIORITY_MANUAL_MAX` của service. Cao hơn `SKU_LIST` (50) để
 * đẩy một nhóm lên trên mọi nhóm tự chấm điểm, nhưng có trần để số không thành vô nghĩa.
 */
export const PRIORITY_MANUAL_MAX = 1000

/**
 * Trạng thái xác nhận định mức của một nhóm — khớp `GROUP_VERIFY_STATUSES` của service.
 *
 * ⚠️ Đây là NHÃN cho người đọc, KHÔNG phải cờ bật/tắt dữ liệu: nhóm `unverified` vẫn được service
 *    dùng để tính giờ. Đừng lọc nó ra khỏi bảng hay khỏi phép tính — làm vậy là biến "chưa ai đối
 *    chiếu" thành "không có định mức", hai chuyện khác nhau hoàn toàn.
 */
export const GROUP_VERIFY_STATUSES = ['unverified', 'verified']

export const GROUP_VERIFY_LABELS = {
    unverified: 'Chưa verify',
    verified: 'Đã verify',
}

/** Trần độ dài ghi chú của một nhóm — khớp `GROUP_NOTE_MAX_LENGTH` của service. */
export const GROUP_NOTE_MAX_LENGTH = 500

/** Trần độ dài TÊN nhóm — khớp `GROUP_NAME_MAX_LENGTH` của service. */
export const GROUP_NAME_MAX_LENGTH = 80

/**
 * Tên hiển thị CỐ ĐỊNH của nhóm mọi-SKU + mọi-NVL.
 *
 * Nhóm đó không đặt tên riêng được (service trả 400 VAL-19), nên tên nằm ở đây chứ không ở DB. Để ở
 * hằng vì có HAI chỗ in nó ra — header thẻ nhóm (`GroupCard`) và nhãn cảnh báo (`warningLabel` ở
 * `StageConfigPage`) — và hai chỗ gọi nó bằng hai tên khác nhau thì người dùng không nối được dòng
 * cảnh báo với thẻ trên màn hình.
 */
export const DEFAULT_GROUP_LABEL = 'Nhóm mặc định'

/**
 * Độ dài tối thiểu của lý do thay đổi chỉ số (VAL-05).
 *
 * ⚠️ PHẢI khớp `assertGroupsWritable` phía service (`changeReason.trim().length < 10`). Đây là bản
 *    sao của một luật ở service — chấp nhận, vì nút Ghi phải chấm được ngay trên máy người dùng
 *    thay vì bắt họ bấm rồi ăn lỗi 400. Đổi một bên phải đổi bên kia.
 *
 *    Để ở đây chứ không ở màn nào: hai màn cùng gác luật này (`ImportExportPage` cho luồng nhập
 *    Excel, `ChangeReasonModal` cho luồng sửa tay). Trước đó mỗi màn giữ một con số riêng, trong đó
 *    một chỗ viết cứng `10` — service nâng ngưỡng là một trong hai màn lệch mà không ai thấy.
 */
export const MIN_CHANGE_REASON = 10

/**
 * Giờ làm thực tế (FAC-26417 FR-001) — PHẢI khớp `ACTUAL_HOURS_STEP` / `ACTUAL_HOURS_MAX` của
 * service (`database/schemas/actual-hours.schema.ts`). Lại là một bản sao của luật ở service, cùng
 * lý do với `MIN_CHANGE_REASON`: ô nhập phải chấm được ngay trên máy người dùng thay vì để họ gõ
 * xong cả tuần rồi ăn một lỗi 400 không nói rõ ô nào sai.
 *
 * Trần là 24 chứ không phải 8: tăng ca có thật, và chặn ở 8 là ép người nhập khai sai.
 */
export const ACTUAL_HOURS_STEP = 0.5
export const ACTUAL_HOURS_MAX = 24

/**
 * Trần số NGÀY của một lượt xem lưới.
 *
 * ⚠️ PHẢI khớp `ACTUAL_HOURS_MAX_RANGE_DAYS` của service. Service CŨNG chặn (thêm 28/10/2026) —
 *    chặn ở đây chỉ để người dùng biết ngay khi chọn thay vì gọi API rồi ăn 400.
 *
 * Nới 31 → 92 ngày (28/10/2026), sau khi đường đọc sản lượng chuyển sang gom tại Mongo: đo trên
 * prod MCVN, lưới 92 ngày từ **65,4 s** xuống **8,4 s**, còn 30 ngày từ 27,6 s xuống 3,8 s. Trần
 * cũ 31 được đặt khi một quý là 65 giây — tức nó là trần của ĐƯỜNG ĐỌC, không phải của bảng.
 *
 * Bảng vẫn cuộn ngang 92 cột và vẫn dựng từng ấy ô nhập; đó là cái giá người dùng tự thấy và tự
 * chọn, khác hẳn một màn hình treo một phút không nói gì.
 */
export const ACTUAL_HOURS_MAX_DAYS = 92

/** Trạng thái một ô trên lưới — khớp `ActualHoursCell['status']` của service. */
export const ACTUAL_HOURS_CELL_LABELS = {
    filled: 'Đã nhập',
    missing: 'Có sản lượng nhưng chưa nhập giờ',
    idle: 'Không có sản lượng',
    future: 'Ngày tương lai, chưa nhập được',
}

/** Ngưỡng bắt nhập lý do khi sửa chỉ số (BR-M16-05 / VAL-05). */
export const RATE_CHANGE_REASON_THRESHOLD = 0.3

/**
 * Số chữ số thập phân tối đa của chỉ số — **0**, tức chỉ số luôn là SỐ NGUYÊN, service làm tròn LÊN.
 *
 * ⚠️ Hằng này từng ghi 4 sau khi service đã chốt 0: FE cho gõ số lẻ, người dùng nhập 0,4 rồi service
 *    ghi 1 — sai 150% mà không ai báo. Đổi ở service thì phải đổi ở đây.
 */
export const RATE_MAX_DECIMALS = 0

/**
 * Mã LUẬT M16 (`M16_VALIDATION_CODES` của service) — nằm trong từng phần tử `data[]` của lỗi, KHÔNG
 * phải mã envelope. Đừng gõ chuỗi tay ở chỗ phân nhánh: gõ nhầm ra một nhánh chết, y như khi so nhầm
 * với mã envelope.
 */
export const VAL_CODES = {
    /** Lệch chỉ số > 30% so với bản đang hiệu lực — phải khai lý do. */
    RATE_CHANGE_REASON_REQUIRED: 'VAL-05',
    /** Ngày hiệu lực ở quá khứ — CẢNH BÁO, không chặn. */
    EFFECTIVE_FROM_IN_PAST: 'VAL-08',
    /** Hai nhóm cùng độ ưu tiên có thể cùng khớp một dòng — cấu hình mơ hồ. */
    AMBIGUOUS_PAIR: 'VAL-09',
    /** `effectiveTo` <= `effectiveFrom` — khoảng ngược hoặc rỗng. */
    EFFECTIVE_RANGE_INVALID: 'VAL-18',
    /** Nhóm mọi-SKU + mọi-NVL đặt tên riêng — nó là nhóm mặc định, tên cố định ở giao diện. */
    GROUP_NAME_NOT_ALLOWED: 'VAL-19',
}

/** Trạng thái độ phủ của một SKU. `missing_routing` là cảnh báo RIÊNG (BR-M16-27). */
export const SKU_COVERAGE_STATUS = {
    covered: 'covered',
    incomplete: 'incomplete',
    missing_routing: 'missing_routing',
}

/** Nhóm thời gian cam kết của M17 (A3). */
export const DUE_GROUPS = [
    {value: 'd1', text: 'Commit 1 ngày', days: 1},
    {value: 'd2', text: 'Commit 2 ngày', days: 2},
    {value: 'd3', text: 'Commit 3 ngày', days: 3},
    {value: 'gt3', text: 'Commit > 3 ngày', days: 4},
]

/**
 * Ba nguồn lấy hàng của M17 (A2 + FAC-M17-SRS-002 FR-M17-17).
 *
 * Thanh chọn nguồn dựng bằng `Object.keys(...)`, nên thêm khoá ở đây là có thêm nút — đừng thêm
 * nhánh `if` nào ở `WorkloadPage`. Thứ tự khoá CHÍNH LÀ thứ tự nút trên màn hình.
 */
export const WORKLOAD_SOURCES = {
    unbatched: 'Chưa phân lô',
    received: 'Thống kê theo thời gian',
    currentOutput: 'Sản lượng hiện tại',
}

/** Mặc định tham số tính toán M17 (BR-M17-41) và khoảng hợp lệ (§6.3 / V-05 → V-09). */
export const WORKLOAD_PARAM_DEFAULTS = {
    shiftsPerDay: 1,
    hoursPerShift: 8,
    personUtilization: 0.85,
    machineUtilization: 0.9,
    peoplePerMachine: 1,
    reworkRate: 0.05,
}

export const WORKLOAD_PARAM_RANGES = {
    shiftsPerDay: {min: 1, max: 3, step: 1, label: 'Số ca / ngày',
        error: 'Số ca mỗi ngày trong khoảng 1 đến 3'},
    hoursPerShift: {min: 1, max: 12, step: 0.5, label: 'Giờ / ca',
        error: 'Số giờ mỗi ca trong khoảng 1 đến 12'},
    personUtilization: {min: 0.3, max: 1, step: 0.05, label: 'Tỷ lệ giờ làm việc hữu ích — người',
        error: 'Tỷ lệ giờ làm việc hữu ích trong khoảng 0,3 đến 1,0'},
    machineUtilization: {min: 0.3, max: 1, step: 0.05, label: 'Tỷ lệ giờ làm việc hữu ích — máy',
        error: 'Tỷ lệ giờ làm việc hữu ích trong khoảng 0,3 đến 1,0'},
    peoplePerMachine: {min: 0.25, max: 3, step: 0.25, label: 'Số người trên một máy',
        error: 'Số người trên một máy trong khoảng 0,25 đến 3,0'},
    // "mặc định" chứ không còn "dùng chung": từng công đoạn đặt được tỷ lệ riêng ngay trên bảng kết
    // quả, và giá trị này chỉ áp cho công đoạn nào chưa đặt.
    reworkRatePercent: {min: 0, max: 50, step: 0.5, label: 'Tỷ lệ làm lại (%) — mặc định',
        error: 'Tỷ lệ làm lại trong khoảng 0 đến 50%'},
}

/**
 * Nội dung bắt buộc của tooltip A7.3 / A7.4 và hộp cảnh báo A7.7 (M17 §3.1.1).
 * Không được rút gọn: đặt sai tham số này làm sai toàn bộ số người ở mọi công đoạn.
 */
export const UTILIZATION_WARNING = {
    full: [
        'Chỉ 9 công đoạn trong quy trình sản xuất mới có chỉ số tiêu chuẩn. Ngoài 9 công đoạn đó, công nhân còn tiêu thời gian cho những việc không có định mức và không được đếm ở bất cứ đâu trong hệ thống: ghi nhận lỗi và chụp ảnh lỗi, đi lấy hàng và trả hàng giữa các công đoạn, chờ hàng từ công đoạn trước, chuẩn bị và vệ sinh máy, họp đầu ca, nghỉ giải lao.',
        'Toàn bộ nhóm việc này được hấp thụ vào duy nhất tham số Tỷ lệ thời gian làm việc hữu ích. Vì vậy tham số này không phải tỷ lệ “trừ giờ nghỉ giải lao” — nó là tỷ lệ giữa thời gian thực sự làm việc có định mức trên tổng thời gian có mặt tại xưởng.',
        'Hệ quả khi đặt sai: nếu đặt theo nghĩa “trừ nghỉ giải lao” (khoảng 85–90%), số người tính ra sẽ thiếu một cách hệ thống ở mọi công đoạn, vì phần công việc gián tiếp không được bù vào đâu cả. Giá trị đúng phải được đo từ thực tế xưởng, và thường thấp hơn con số trực giác.',
    ],
    person: 'Chỉ 9 công đoạn trong quy trình sản xuất mới có chỉ số tiêu chuẩn. Toàn bộ việc không có định mức được hấp thụ vào duy nhất tham số này. Hệ quả khi đặt sai: đặt theo nghĩa “trừ nghỉ giải lao” thì số người tính ra sẽ thiếu một cách hệ thống ở mọi công đoạn.',
    machine: 'Tương tự nhưng cho máy: trừ thời gian bảo trì và cài đặt máy.',
}

/** Mô tả ba nguồn lấy hàng (A8) — M17 §3.1.2, bắt buộc hiển thị trên màn hình. */
export const WORKLOAD_SOURCE_DESCRIPTIONS = {
    received: 'Thống kê theo thời gian — lượng item của toàn bộ order được tạo tại FAC trong khoảng thời gian đã chọn. Dùng cho hai việc: (a) tính cần bao nhiêu giờ và bao nhiêu công nhân cho lượng hàng mới tạo ở FAC, gồm cả hàng chưa sản xuất lẫn hàng đang sản xuất một phần; (b) kiểm chứng ngược — chọn một khoảng thời gian trong quá khứ để tính thời gian tiêu chuẩn của lượng đơn đó rồi so với thực tế đã trải qua, nhằm rút kinh nghiệm sắp xếp nguồn lực.',
    unbatched: 'Chưa phân lô — lượng item thực tế trên FAC chưa được đẩy vào bất cứ lô sản xuất nào. Dùng để ước tính thời gian và nhân lực cần để hoàn thành lượng item đó.',
    // Nguyên văn FR-M17-17 bước 3 — AC-17.3 kiểm ĐÚNG TỪNG CHỮ, không được rút gọn.
    currentOutput: 'Sản lượng hiện tại — toàn bộ item đang tồn trên FAC chưa hoàn thành sản xuất, lấy đúng như bảng Thời gian sắp / đã quá hạn đang thống kê, phân loại theo số ngày sản xuất còn lại hoặc số ngày đã quá hạn. Dùng để lập kế hoạch nhân lực theo mức khẩn: chọn muốn làm hết bao nhiêu phần trăm của từng nhóm ngày, hệ thống tính ra cần bao nhiêu người và bao nhiêu máy ở mỗi công đoạn.',
}

/**
 * Dải cảnh báo BẮT BUỘC của nguồn Sản lượng hiện tại (FR-M17-23, BR-N3-18).
 *
 * Luôn hiện khi chọn nguồn này, không có nút tắt, và KHÔNG phụ thuộc hai tỷ lệ chất lượng dữ liệu
 * (AC-23.1). Lý do nó bắt buộc: nguồn này chứa nhiều hàng đang làm dở hơn hẳn hai nguồn cũ, nên đọc
 * con số như "phần việc còn lại" sẽ kết luận thiếu người gấp đôi thực tế.
 */
export const CURRENT_OUTPUT_WARNING =
    'Sản lượng hiện tại gồm cả hàng đang sản xuất dở, đặc biệt ở các nhóm đã quá hạn. Báo cáo tính đủ toàn bộ quy trình cho mọi item, không trừ phần đã làm. Vì vậy giờ công và số người ở đây là mức trần, cao hơn phần việc thực sự còn lại. Dùng để so sánh tương quan giữa các công đoạn và giữa các kịch bản tỷ lệ, không dùng làm con số tuyệt đối.'

/**
 * Ngưỡng cảnh báo lệch giữa số thực tế và số mục tiêu (BR-N3-15).
 *
 * 15% chứ không phải con số trực giác nhỏ hơn: nguyên tắc lấy trọn đơn tự nó đã sinh ra mức vượt
 * đáng kể, đặt thấp thì cảnh báo bật liên tục và mất tác dụng.
 */
/**
 * Giải thích "Khung thời gian mục tiêu" — đây là MẪU SỐ của toàn bộ phép tính nhân lực, nên hiểu sai
 * nó là sai mọi con số người/máy trên màn hình.
 */
export const TARGET_DAYS_TOOLTIP =
    'Muốn làm xong lượng hàng đang chọn trong bao nhiêu NGÀY LÀM VIỆC. Đây là mẫu số của cả phép tính: tổng giờ công chia cho số giờ hữu ích mà một người làm được trong khung này ra số người cần bố trí. Đặt 1 nghĩa là "làm xong trong hôm nay" nên cần nhiều người nhất; đặt 3 thì cùng lượng hàng đó chỉ cần khoảng một phần ba số người. Không phải hạn giao hàng, cũng không phải thời gian đã trôi qua — chỉ là khoảng thời gian bạn muốn dồn lượng hàng này vào.'

export const ALLOCATION_OVERSHOOT_WARN = 0.15

/** Màu tiêu đề ba nhóm mức khẩn — phải TRÙNG màn hình nguồn để đối chiếu bằng mắt (SRS-002 §5). */
export const URGENCY_GROUP_COLORS = {
    green: '#1a7a1a',
    amber: '#c79a06',
    red: '#9d1b13',
}

/** Ba nhãn trạng thái dòng chi tiết của M18 (§H.4). */
export const OUTPUT_STATUS_LABELS = {
    computed: {text: 'Đã tính giờ', color: 'green'},
    no_standard: {text: 'Thiếu định mức', color: 'orange'},
    no_file_data: {text: 'Thiếu dữ liệu file in', color: 'red'},
}

/**
 * Nhãn + màu của bốn hạng hiệu suất (FAC-26417, PO câu 13).
 *
 * ⚠️ Màu lấy đúng bốn token đang có của nhóm màn, KHÔNG thêm màu mới: A `$vc-success` ·
 *    B `$vc-primary` · C `$vc-warning` · D `$vc-danger`. Tên `green/blue/orange/red` là tên preset
 *    của antd `Tag`, cùng quy ước với `OUTPUT_STATUS_LABELS` ngay dưới.
 *
 * ⚠️ `N` KHÔNG phải hạng thứ năm — nó là "chưa đủ điều kiện xếp hạng", và phải trông khác hẳn bốn
 *    hạng kia. Tô nó cùng bảng màu là biến "chưa biết" thành một lời đánh giá.
 */
export const GRADE_LABELS = {
    A: {text: 'A', title: 'Tốt', color: 'green'},
    B: {text: 'B', title: 'Đạt', color: 'blue'},
    C: {text: 'C', title: 'Cần cải thiện', color: 'orange'},
    D: {text: 'D', title: 'Cần hỗ trợ', color: 'red'},
    N: {text: '—', title: 'Chưa xếp hạng', color: ''},
}

/** Ba lý do loại dòng log — tách rời vì là ba nguyên nhân khác nhau (BR-A03). */
export const DROP_REASON_LABELS = {
    unmapped_action: 'Mã thao tác chưa khai báo trong Cấu hình chỉ số tiêu chuẩn',
    stage_out_of_scope: 'Thuộc công đoạn ngoài 9 công đoạn của dòng decor',
    barcode_unresolved: 'Không tra được barcode về sản phẩm',
}

/** Người đi xử lý từng loại thiếu dữ liệu — UI-08: lỗi không có người nhận thì nằm im. */
export const OWNERS = {
    standard: 'IE / Trưởng khu vực',
    fileData: 'Designer / File operator',
}
